// ── PremiumGateModal ──────────────────────────────────────────────────────────
// The full "Upgrade to Premium" page — opened from every "this is a Premium
// feature" prompt in the app (App Blocker, Attachments, Journal, Focus
// charts, Profile, and the Settings panel's PremiumRow), so there's one
// purchase flow instead of several. Keeps the old (feature, onClose) props so
// none of those call sites needed to change.
//
// On native Android this shows Google Play's own localized price per plan
// (via usePlayBillingPrices() in playBilling.js) — never our own PHP/USD
// guess, since Play's purchase sheet is the only thing that can actually
// charge the buyer, and it localizes automatically per country. Off Android,
// startCheckout() already branches to PayMongo (PH) or Lemon Squeezy
// (everyone else), same as before.
//
// Lifetime (pay once) is the fourth option everywhere: a one-time Play
// purchase on Android, a one-time PayMongo / Lemon Squeezy checkout on the web.
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, ChevronDown, Crown, BellRing, BarChart3, ShieldBan, LayoutGrid, Palette, RefreshCw, ShieldCheck, Smartphone, Infinity as InfinityIcon } from 'lucide-react';
import { toast } from 'sonner';
import { startCheckout, usePremium, isNativeAndroid, guessIsPH, detectCountryInfo, isLemonSqueezyEnabled, joinLemonSqueezyWaitlist } from '../firebase/billing';
import { usePlayBillingPrices, playPriceAmounts, usePlayPurchasePending } from '../firebase/playBilling';
import { PLANS, PLAN_ORDER, LIFETIME, discountedUsd, yearlySavingsPercent, yearlyPerMonthLabel } from '../firebase/plans';
import { PREMIUM_EXTRAS, FREE_FOR_EVERYONE, ANDROID_ONLY_SUMMARY } from '../firebase/premiumFeatures';
import { premiumFaqs } from '../firebase/premiumFaqs';
import { ONBOARDING_VARS, OB_BLUE, OB_VIOLET, OB_INK, OB_PINK, OB_STRONG, OB_BTN_PINK, OB_BTN_GRADIENT } from '../theme/onboardingPalette';

// Same look as the sign-in page (Jade, 2026-10-11): its fixed blue / lavender /
// pink palette instead of the app's accent colour, white background with a
// faint dot grid and the logo-coloured waves, and borderless white cards with
// a soft shadow. ONBOARDING_VARS is set on this page's own root (it's
// portalled to <body>), so any var(--c-*) inside reads that palette.
const BTN_GRADIENT = OB_BTN_GRADIENT;
const SOFT = '#333333';
const ACCENT = OB_PINK; // the palette's accent blue (named pink for history)
const RULE = '#EEF0FB'; // hairline between rows of a list
// Two layers (Jade, 2026-10-11: more depth so each card's shape reads): a tight
// one that outlines the edge, and a wide soft one underneath.
const SHADOW = '0 2px 6px rgba(92,104,180,0.10), 0 12px 28px -6px rgba(110,124,210,0.32)';
const CARD = { background: '#fff', borderRadius: 18, boxShadow: SHADOW };
const WAVES = [
  { color: OB_BLUE, opacity: 0.55, d: 'M0,70 C80,30 160,30 240,60 C300,82 350,70 400,46 L400,160 L0,160 Z' },
  { color: OB_VIOLET, opacity: 0.6, d: 'M0,98 C70,72 150,70 220,92 C290,114 340,108 400,84 L400,160 L0,160 Z' },
  { color: OB_BTN_PINK, opacity: 0.65, d: 'M0,128 C90,104 170,110 250,124 C320,136 360,128 400,114 L400,160 L0,160 Z' },
];
const DOT_GRID = {
  position: 'absolute', top: 0, left: 0, right: 0, height: 260,
  backgroundImage: `radial-gradient(${OB_VIOLET} 1.4px, transparent 1.6px)`,
  backgroundSize: '18px 18px',
  WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
  maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
};

// Done once in idle time after the app starts, so the first Upgrade tap doesn't
// pay for it inside the fade-in (Jade saw it stutter after a reload): the
// country guess and price formatter (both load Intl data the first time), and
// every Quicksand weight the page uses (a weight first used mid-open is
// downloaded then and forces a second layout).
let warmed = false;
export function warmPremiumPage() {
  if (warmed || typeof window === 'undefined') return;
  warmed = true;
  try {
    guessIsPH();
    yearlyPerMonthLabel({ yearly: { value: 999, currency: 'PHP' } });
    yearlyPerMonthLabel({ yearly: { value: 29.99, currency: 'USD' } });
    const f = document.fonts;
    if (f) [400, 500, 600, 700, 800].forEach((w) => f.load(`${w} 16px Quicksand`).catch(() => null));
  } catch { /* warm-up only */ }
}

// Five headline perks under the title, so the page says what Premium is for
// before any price. Each one groups related PREMIUM_EXTRAS rows (the full list
// is further down); keep them in step if an extra is added or moved.
const HIGHLIGHTS = [
  { Icon: BellRing, label: 'Smart alerts', hint: 'Alarms & unlimited notifications' },  // Alarms & unlimited notifications
  { Icon: BarChart3, label: 'Deeper insights', hint: 'Habit stats & time history' },       // habit statistics, time allocation month/year
  { Icon: ShieldBan, label: 'Focus mode', hint: 'Block distracting apps' },                // app blocker
  { Icon: LayoutGrid, label: 'Widgets & sync', hint: 'More widgets, Google Calendar' },    // widgets, Google Calendar sync
  { Icon: Palette, label: 'Make it yours', hint: 'Backgrounds, stickers & more' },         // backgrounds, sticker maker, journal exports, bigger attachments
];

// One tap-to-open row, shared by "Premium extras" and the FAQ.
function DropdownRow({ label, children, first, open, onToggle, bullet, tag }) {
  return (
    <li style={{ borderTop: first ? 'none' : `1px solid ${RULE}` }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
          background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
          fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: OB_INK, lineHeight: 1.35,
        }}
      >
        {bullet && <span style={{ flexShrink: 0, color: OB_STRONG, fontWeight: 800 }}>{bullet}</span>}
        <span style={{ flex: 1 }}>
          {label}
          {tag && (
            <span style={{
              display: 'inline-block', marginLeft: 6, padding: '1px 7px', borderRadius: 999, verticalAlign: 1,
              fontSize: 10, fontWeight: 700, color: OB_PINK, background: '#EEF0FF', whiteSpace: 'nowrap',
            }}>{tag}</span>
          )}
        </span>
        <ChevronDown size={18} color={OB_STRONG} style={{ flexShrink: 0, transition: 'transform 0.2s ease', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      {open && (
        <p style={{ margin: 0, padding: bullet ? '0 14px 12px 38px' : '0 14px 12px', fontSize: 12.5, lineHeight: 1.5, color: SOFT }}>
          {children}
        </p>
      )}
    </li>
  );
}

const sectionTitle = { margin: '0 0 10px', fontSize: 15, fontWeight: 800, color: OB_INK };
const listCard = {
  margin: 0, listStyle: 'none', padding: 0, overflow: 'hidden',
  ...CARD,
};

export default function PremiumGateModal({ onClose, onPurchased, initialPlan }) {
  const [busy, setBusy] = useState(false);
  // Labels of the Premium-extras rows that are tapped open.
  const [openExtras, setOpenExtras] = useState([]);
  const [openFaq, setOpenFaq] = useState([]);
  // The fade starts only once the page has been drawn (two frames after it
  // mounts). Its first draw is the slow part, and a fade started before it
  // spent its first frames frozen, which read as a stutter (Jade). The class is
  // swapped on the element directly so it costs no re-render.
  const rootRef = useRef(null);
  useEffect(() => {
    let r2 = 0;
    const r1 = requestAnimationFrame(() => {
      r2 = requestAnimationFrame(() => rootRef.current?.classList.replace('clarity-premium-wait', 'clarity-premium-fadein'));
    });
    return () => { cancelAnimationFrame(r1); cancelAnimationFrame(r2); };
  }, []);
  const [provider, setProvider] = useState(() => (guessIsPH() ? 'paymongo' : 'lemonsqueezy'));
  const nativeAndroid = isNativeAndroid();
  const [plan, setPlan] = useState(initialPlan || 'monthly');
  // A subscriber opening this to switch to Lifetime needs telling that the
  // subscription doesn't stop by itself (see the footer note). Only Play and
  // Lemon Squeezy renew; a PayMongo payment never does.
  const { isPremium, via, loading: premiumLoading } = usePremium();
  const planIds = [...PLAN_ORDER, LIFETIME.id];
  const cancelOldSubNote = !isPremium ? ''
    : via === 'play' ? " Your current subscription doesn't stop by itself: cancel it in the Play Store after buying."
    : via === 'lemonsqueezy' ? " Your current subscription doesn't stop by itself: cancel it from the link in your Lemon Squeezy receipt email."
    : '';
  const [waitlistBusy, setWaitlistBusy] = useState(false);
  const [waitlistJoined, setWaitlistJoined] = useState(false);
  const playPrices = usePlayBillingPrices();
  // Play policy forbids showing an alternative payment method inside the app
  // on Android, so the PHP/USD price guess and the Lemon-Squeezy-specific
  // "coming soon" waitlist path must never render there — only the plan
  // picker and a single Upgrade button, which startCheckout() already routes
  // to Play Billing for this platform.
  // Purchasing-power discount % the server will apply at Lemon Squeezy checkout
  // for this visitor's country (0 = none). Only ever shown on the USD rail.
  const [discountPercent, setDiscountPercent] = useState(0);
  const showComingSoon = !nativeAndroid && provider === 'lemonsqueezy' && !isLemonSqueezyEnabled();

  // Replace the instant timezone/locale guess with the real IP-based answer
  // once it comes back — silent, no user-facing toggle (see api/geo.js).
  useEffect(() => {
    if (nativeAndroid) return;
    let cancelled = false;
    detectCountryInfo().then((info) => {
      if (cancelled || !info) return;
      if (info.country) setProvider(info.country === 'PH' ? 'paymongo' : 'lemonsqueezy');
      setDiscountPercent(info.discountPercent || 0);
    });
    return () => { cancelled = true; };
  }, [nativeAndroid]);

  // The page leaves by itself once a purchase goes through (Jade, 2026-10-28).
  // Paying through Google Play with GCash hops out to GCash and back ("Back to
  // merchant"), and you landed on this same page again, with the Settings
  // Premium row ("Confirming", then "Active") hidden under it. It now closes
  // as soon as a purchase started here is being confirmed, or Premium turns on
  // while it's open (also a web payment landing). onPurchased lets a caller
  // that isn't Settings show Settings instead; Settings itself just closes it.
  const purchasePending = usePlayPurchasePending();
  const startedRef = useRef(false);
  const wasPremiumRef = useRef(null); // null until the first settled answer
  const leaveRef = useRef(null);
  leaveRef.current = onPurchased || onClose;
  useEffect(() => {
    if (premiumLoading) return;
    const was = wasPremiumRef.current;
    wasPremiumRef.current = isPremium;
    if ((was === false && isPremium) || (startedRef.current && purchasePending)) leaveRef.current();
  }, [isPremium, premiumLoading, purchasePending]);

  const handleUpgrade = async () => {
    setBusy(true);
    // Set before the sheet opens: Play can report the purchase before
    // startCheckout() itself returns.
    startedRef.current = true;
    try {
      await startCheckout(provider, plan);
    } catch (e) {
      startedRef.current = false;
      if (e.message !== 'cancelled') toast.error(e.message || 'Could not start checkout. Please try again.');
    } finally {
      // startCheckout() resolves once the purchase flow has STARTED (the
      // Play Billing sheet shown, or the hosted checkout tab opened) — not
      // once the purchase is complete. Without this, a successful call left
      // `busy` stuck true forever, so the button showed its loading state
      // indefinitely even after a real, successful purchase.
      setBusy(false);
    }
  };

  const handleJoinWaitlist = async () => {
    setWaitlistBusy(true);
    try {
      await joinLemonSqueezyWaitlist();
      setWaitlistJoined(true);
      toast.success("You're on the list. We'll let you know when it's ready!");
    } catch (e) {
      toast.error(e.message || 'Could not join the waitlist. Please try again.');
    } finally {
      setWaitlistBusy(false);
    }
  };

  const priceLabel = (id) => {
    const p = id === LIFETIME.id ? LIFETIME : PLANS[id];
    if (nativeAndroid) return playPrices[id] || '…';
    if (provider === 'paymongo') return `₱${p.php}`;
    return discountPercent ? `$${discountedUsd(p.usd, discountPercent).toFixed(2)}` : `$${p.usd}`;
  };

  // Shown next to a discounted price: the full price, struck through.
  const fullPriceLabel = (id) => {
    const p = id === LIFETIME.id ? LIFETIME : PLANS[id];
    return nativeAndroid || provider === 'paymongo' || !discountPercent ? null : `$${p.usd}`;
  };

  // Plain amounts in the currency shown, for the "Save N%" badge and the
  // Yearly "per month" line (Play's own amounts on Android).
  const amounts = (() => {
    if (nativeAndroid) return playPriceAmounts();
    const out = {};
    planIds.forEach((id) => {
      const p = id === LIFETIME.id ? LIFETIME : PLANS[id];
      out[id] = provider === 'paymongo'
        ? { value: p.php, currency: 'PHP' }
        : { value: discountPercent ? discountedUsd(p.usd, discountPercent) : p.usd, currency: 'USD' };
    });
    return out;
  })();
  const yearlySave = yearlySavingsPercent(amounts);
  const yearlyPerMonth = yearlyPerMonthLabel(amounts);
  const badgeFor = (id) => (id === 'yearly' ? (yearlySave ? `Best value · Save ${yearlySave}%` : 'Best value')
    : id === LIFETIME.id ? 'Pay once' : null);

  // Small reassurances under the button. What "cancel" means depends on the
  // plan and the payment rail (a PayMongo payment never renews).
  const renews = plan !== LIFETIME.id && (nativeAndroid || provider === 'lemonsqueezy');
  const trustItems = [
    plan === LIFETIME.id ? { Icon: InfinityIcon, text: 'Nothing renews' }
      : renews ? { Icon: RefreshCw, text: 'Cancel anytime' }
      : { Icon: RefreshCw, text: 'No auto-renewal' },
    { Icon: ShieldCheck, text: nativeAndroid ? 'Via Google Play' : 'Secure payment' },
    { Icon: Smartphone, text: 'All your devices' },
  ];

  // Questions for the rail this person pays through (see premiumFaqs.js).
  const faqs = premiumFaqs(nativeAndroid ? 'play' : provider, { comingSoon: showComingSoon });

  const periodLabel = (id) => (id === LIFETIME.id ? 'one-time' : id === 'weekly' ? '/ week' : id === 'monthly' ? '/ month' : '/ year');

  return createPortal(
    <div
      ref={rootRef}
      className="clarity-premium-wait"
      style={{
        ...ONBOARDING_VARS,
        // Above the Habits tab's own FAB (zIndex: 99999 in HabitsTab.jsx) —
        // a full-page takeover has to cover every floating button, not just
        // the usual modal layer.
        position: 'fixed', inset: 0, zIndex: 100000,
        background: '#fff', display: 'flex', flexDirection: 'column',
        fontFamily: "'Quicksand', 'Nunito', sans-serif",
      }}
    >
      {/* App.jsx's global `::-webkit-scrollbar { width: 4px }` rule matches
          every scroller (see CLAUDE.md), which puts this one into persistent
          custom-scrollbar mode instead of Android/desktop's normal transient
          overlay indicator — needs !important to actually turn off here. */}
      <style>{`
        .clarity-premium-scroll::-webkit-scrollbar { width: 0 !important; height: 0 !important; display: none !important; }
        .clarity-premium-scroll { scrollbar-width: none !important; }
        @keyframes clarityPremiumFadeIn { from { opacity: 0; } to { opacity: 1; } }
        .clarity-premium-wait { opacity: 0; }
        .clarity-premium-fadein { animation: clarityPremiumFadeIn 0.22s ease; }
      `}</style>
      {/* Sign-in page decoration: dot grid at the top, logo-coloured waves
          along the bottom (they show through the frosted footer). */}
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <div style={DOT_GRID} />
        <svg viewBox="0 0 400 160" preserveAspectRatio="none"
          style={{ position: 'absolute', left: 0, bottom: 0, width: '100%', height: 'min(150px, 14vh)' }}>
          {WAVES.map((w, i) => <path key={i} d={w.d} fill={w.color} opacity={w.opacity} />)}
        </svg>
      </div>
      {/* Close sits at the corner of the whole SCREEN, not the centered
          column below — on a wide desktop viewport the column no longer
          reaches the edge, so anchoring the button to it looked stranded
          in the middle of empty space instead of where a close control is
          expected. */}
      <button
        onClick={() => !busy && onClose()}
        aria-label="Close"
        style={{
          position: 'absolute', top: 'calc(var(--sat, 0px) + 16px)', left: 16, zIndex: 1,
          width: 34, height: 34, borderRadius: '50%', border: 'none',
          background: '#fff', color: OB_INK, boxShadow: SHADOW,
          display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
        }}
      >
        <X size={18} />
      </button>

      {/* Capped width + centered so this reads as a page, not a full-bleed
          banner, on wide (desktop) viewports — on phone widths this is a
          no-op since maxWidth is well above the viewport. */}
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%', maxWidth: 640, margin: '0 auto' }}>
        <div className="clarity-premium-scroll" style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: 'calc(var(--sat, 0px) + 64px) 0 16px' }}>
          <div style={{ padding: '0 22px 0', textAlign: 'center' }}>
            <div style={{
              position: 'relative', width: 64, height: 64, margin: '0 auto 14px', borderRadius: '50%',
              background: BTN_GRADIENT, border: '3px solid #fff', boxSizing: 'border-box',
              boxShadow: '0 8px 26px rgba(186,170,236,0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Crown size={28} color={OB_INK} strokeWidth={2} />
            </div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: OB_INK }}>
              Turn your vision into Clarity
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: 15, color: SOFT }}>
              Level up your routine and stay on track.
            </p>
            {/* 3 on the first row, 2 centred under them. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 10, marginTop: 18 }}>
              {HIGHLIGHTS.map(({ Icon, label, hint }) => (
                <div key={label} style={{
                  width: 'calc((100% - 20px) / 3)', boxSizing: 'border-box',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
                  padding: '12px 6px', ...CARD, borderRadius: 16,
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%', background: '#EEF0FF',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon size={17} color={OB_STRONG} strokeWidth={2.3} />
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.2, color: OB_INK }}>{label}</div>
                  <div style={{ fontSize: 10.5, lineHeight: 1.3, color: SOFT }}>{hint}</div>
                </div>
              ))}
            </div>
            {/* On the website: say before anyone pays that some extras need the
                Android app (there is no iPhone app). Not needed in the app itself. */}
            {!nativeAndroid && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 14, padding: '10px 12px',
                borderRadius: 14, background: '#EEF0FF', textAlign: 'left',
              }}>
                <Smartphone size={16} color={OB_PINK} strokeWidth={2.3} style={{ flexShrink: 0, marginTop: 1 }} />
                <span style={{ fontSize: 12, lineHeight: 1.45, color: OB_INK }}>{ANDROID_ONLY_SUMMARY}</span>
              </div>
            )}
          </div>

          <div style={{ padding: '18px 22px 0' }}>
          {showComingSoon ? (
            <div style={{
              padding: '14px 16px', textAlign: 'left', ...CARD,
            }}>
              <div style={{ fontSize: 13, color: OB_INK, lineHeight: 1.5 }}>
                🇵🇭 Premium is available in the Philippines for now, via QR Ph (GCash, Maya and card are coming soon).
                International (USD) payments are coming soon.
              </div>
              <button
                onClick={handleJoinWaitlist}
                disabled={waitlistBusy || waitlistJoined}
                style={{
                  marginTop: 12, width: '100%', background: waitlistJoined
                    ? '#F3F4FB'
                    : BTN_GRADIENT,
                  color: waitlistJoined ? SOFT : OB_INK,
                  border: 'none', borderRadius: 999, fontFamily: 'inherit',
                  padding: '11px', fontSize: 13.5, fontWeight: 700,
                  cursor: (waitlistBusy || waitlistJoined) ? 'default' : 'pointer', opacity: waitlistBusy ? 0.6 : 1,
                }}
              >
                {waitlistJoined ? "You're on the list" : waitlistBusy ? '…' : 'Notify me when it launches'}
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {planIds.map((id) => {
                const active = plan === id;
                const isLifetime = id === LIFETIME.id;
                const badge = badgeFor(id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setPlan(id)}
                    disabled={busy}
                    style={{
                      position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center',
                      textAlign: 'center', borderRadius: 18, padding: '14px 16px', cursor: busy ? 'default' : 'pointer',
                      fontFamily: 'inherit', boxShadow: SHADOW,
                      // No outline when not picked (Jade: cleaner without lines); the
                      // picked plan gets the logo gradient as a ring.
                      border: '2px solid transparent',
                      background: active ? `linear-gradient(#fff, #fff) padding-box, ${BTN_GRADIENT} border-box` : '#fff',
                    }}
                  >
                    {badge && (
                      <div style={{
                        position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
                        whiteSpace: 'nowrap', padding: '2px 10px', borderRadius: 999,
                        fontSize: 10.5, fontWeight: 800, letterSpacing: 0.2,
                        // Plain accent blue, same as the icons (Jade: no gradient here).
                        background: OB_STRONG, color: '#fff',
                        boxShadow: '0 2px 8px rgba(143,166,236,0.35)',
                      }}>
                        {badge}
                      </div>
                    )}
                    <div style={{
                      position: 'absolute', top: '50%', right: 16, transform: 'translateY(-50%)',
                      flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
                      border: active ? 'none' : '2px solid #D3D8F6',
                      background: active ? OB_STRONG : 'transparent', // plain icon blue (Jade)
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {active && <Check size={13} color="#fff" strokeWidth={3} />}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: OB_INK }}>
                      {fullPriceLabel(id) && (
                        <span style={{ fontSize: 12, fontWeight: 600, color: SOFT, textDecoration: 'line-through', marginRight: 6 }}>
                          {fullPriceLabel(id)}
                        </span>
                      )}
                      {priceLabel(id)} <span style={{ fontSize: 12, fontWeight: 600, color: SOFT }}>{periodLabel(id)}</span>
                    </div>
                    {fullPriceLabel(id) && (
                      <div style={{ fontSize: 11, fontWeight: 700, color: ACCENT, marginTop: 1 }}>
                        {discountPercent}% off for your region
                      </div>
                    )}
                    <div style={{ fontSize: 11.5, color: SOFT, marginTop: 2 }}>
                      {isLifetime
                        ? 'Lifetime · pay once, keep Premium forever'
                        : `${PLANS[id].label} · billed every ${PLANS[id].days} days`}
                    </div>
                    {id === 'yearly' && yearlyPerMonth && (
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: ACCENT, marginTop: 2 }}>
                        Just {yearlyPerMonth} a month
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ padding: '22px 22px 0' }}>
          <h2 style={sectionTitle}>Free for everyone</h2>
          <ul style={{
            margin: '0 0 22px', listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 9,
            padding: '14px 16px', ...CARD,
          }}>
            {FREE_FOR_EVERYONE.map((f) => (
              <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: OB_INK, lineHeight: 1.4 }}>
                <span style={{ flexShrink: 0, color: OB_STRONG, fontWeight: 800 }}>✓</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <h2 style={sectionTitle}>Premium extras</h2>
          <ul style={listCard}>
            {PREMIUM_EXTRAS.map((row, i) => {
              const open = openExtras.includes(row.label);
              return (
                <DropdownRow
                  key={row.label} label={row.label} first={!i} open={open} bullet="✓"
                  tag={nativeAndroid ? null : row.androidOnly}
                  onToggle={() => setOpenExtras(open ? [] : [row.label])} // one description open at a time (Jade)
                >
                  {row.description}
                </DropdownRow>
              );
            })}
          </ul>
          <h2 style={{ ...sectionTitle, marginTop: 22 }}>Questions</h2>
          <ul style={listCard}>
            {faqs.map((f, i) => {
              const open = openFaq.includes(f.q);
              return (
                <DropdownRow
                  key={f.q} label={f.q} first={!i} open={open}
                  onToggle={() => setOpenFaq(open ? [] : [f.q])} // one answer open at a time (Jade)
                >
                  {f.a}
                </DropdownRow>
              );
            })}
          </ul>
          <div style={{ margin: '18px 0 4px', textAlign: 'center', fontSize: 12, color: SOFT }}>
            <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: OB_INK, fontWeight: 600 }}>Terms</a>
            {'  ·  '}
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: OB_INK, fontWeight: 600 }}>Privacy Policy</a>
          </div>
        </div>
      </div>

      {/* --sab = the gesture/nav bar height on the Android app (0 in a browser,
          so the web page is unchanged): keeps the button clear of the system buttons. */}
      {!showComingSoon && (
        <div style={{ position: 'relative', padding: '12px 22px calc(22px + var(--sab, 0px))', background: 'rgba(255,255,255,0.72)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', boxShadow: '0 -8px 24px rgba(143,166,236,0.12)' }}>
          <div style={{ marginBottom: 8, fontSize: 12.5, lineHeight: 1.4, textAlign: 'center', color: SOFT }}>
            {plan === LIFETIME.id
              ? `One-time payment${nativeAndroid ? ' through Google Play' : ''}. Nothing renews. Premium stays unlocked for good.${cancelOldSubNote}`
              : nativeAndroid
              ? `Renews automatically every ${PLANS[plan].days} days until you cancel, billed through Google Play. Cancel anytime in the Play Store.`
              : provider === 'lemonsqueezy'
                ? `Renews automatically every ${PLANS[plan].days} days until you cancel. Cancel anytime. You keep the time you already paid for.`
                : `One-time payment for ${PLANS[plan].days} days of Premium. No auto-renewal, no saved card.`}
          </div>
          <button
            onClick={handleUpgrade}
            disabled={busy}
            style={{
              width: '100%', background: BTN_GRADIENT, fontFamily: 'inherit',
              color: OB_INK, border: 'none', borderRadius: 999, boxShadow: '0 4px 16px rgba(143,166,236,0.4)',
              padding: '13px', fontSize: 14.5, fontWeight: 800,
              cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? '…' : 'Unlock premium now'}
          </button>
          <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '4px 14px', marginTop: 10 }}>
            {trustItems.map(({ Icon, text }) => (
              <span key={text} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: SOFT }}>
                <Icon size={12} strokeWidth={2.4} color={OB_STRONG} />
                {text}
              </span>
            ))}
          </div>
        </div>
        )}
      </div>
    </div>,
    document.body
  );
}
